import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './LandingPage.module.css';
import { useTranslation } from '../context/TranslationContext';
import Icon from '../ui/Icon';
import Button from '../ui/Button';

function LandingPage({ isAuthenticated }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleTryApp = () =>
    navigate(isAuthenticated ? '/dashboard' : '/login');

  return (
    <div className={styles.page}>
      {/* Slim top brand row — no sidebar on this page */}
      <header className={styles.topbar}>
        <div className={styles.brand}>
          <span className={styles.brandIcon}>
            <Icon name="fa-scale-balanced" size="lg" strokeWidth={2} />
          </span>
          <span className={styles.brandText}>{t('appName')}</span>
        </div>
        <nav className={styles.topNav}>
          <a href="#features" className={styles.topNavLink}>
            {t('seeFeatures')}
          </a>
          <Button variant="primary" size="sm" onClick={handleTryApp}>
            {t('tryTheApp')}
          </Button>
        </nav>
      </header>

      {/* Hero */}
      <section className={styles.hero}>
        <div className={styles.container}>
          <div className={styles.heroRow}>
            <div className={styles.heroCopy}>
              <span className={styles.eyebrow}>
                <span className={styles.eyebrowDot} />
                {t('aiPoweredFinanceApp')}
              </span>
              <h1 className={styles.title}>{t('takeControlOfYourFinances')}</h1>
              <p className={styles.subtitle}>{t('landingPageSubtitle')}</p>
              <div className={styles.heroActions}>
                <Button variant="primary" size="lg" onClick={handleTryApp}>
                  {t('tryTheApp')}
                </Button>
                <a href="#features" className={styles.linkAction}>
                  {t('seeFeatures')} →
                </a>
              </div>
              <p className={styles.fine}>{t('noCreditCard')}</p>
            </div>

            <aside className={styles.heroAside} aria-hidden="true">
              <article className={styles.previewCard}>
                <header className={styles.previewHeader}>
                  <span className={styles.previewChip}>
                    <Icon name="fa-receipt" size="sm" />
                  </span>
                  <div>
                    <div className={styles.previewTitle}>{t('moduleReceiptsTitle')}</div>
                    <div className={styles.previewSub}>{t('moduleReceiptsSubtitle')}</div>
                  </div>
                </header>
                <div className={`${styles.previewMetric} num`}>₺ 4 217,80</div>
                <div className={styles.previewMuted}>this month</div>
              </article>

              <article className={`${styles.previewCard} ${styles.previewCardOffset}`}>
                <header className={styles.previewHeader}>
                  <span className={`${styles.previewChip} ${styles.previewChipSuccess}`}>
                    <Icon name="fa-credit-card" size="sm" />
                  </span>
                  <div>
                    <div className={styles.previewTitle}>{t('moduleBudgetTitle')}</div>
                    <div className={styles.previewSub}>{t('moduleBudgetSubtitle')}</div>
                  </div>
                </header>
                <div className={`${styles.previewMetric} num`}>₺ 12 480,00</div>
                <div className={styles.previewMuted}>upcoming installments</div>
              </article>

              <article className={`${styles.previewCard} ${styles.previewCardOffsetMore}`}>
                <header className={styles.previewHeader}>
                  <span className={`${styles.previewChip} ${styles.previewChipWarning}`}>
                    <Icon name="fa-chart-pie" size="sm" />
                  </span>
                  <div>
                    <div className={styles.previewTitle}>{t('insightsAndReports')}</div>
                    <div className={styles.previewSub}>{t('insightsAndReportsSubtitle')}</div>
                  </div>
                </header>
                <div className={styles.previewBars}>
                  <span style={{ width: '64%' }} />
                  <span style={{ width: '40%' }} />
                  <span style={{ width: '82%' }} />
                </div>
              </article>
            </aside>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className={styles.steps}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>{t('howItWorks')}</h2>
          <ol className={styles.stepsList}>
            <li className={styles.step}>
              <div className={styles.stepNum}>1</div>
              <h3 className={styles.stepTitle}>{t('stepUpload')}</h3>
              <p className={styles.stepDesc}>{t('stepUploadDesc')}</p>
            </li>
            <li className={styles.step}>
              <div className={styles.stepNum}>2</div>
              <h3 className={styles.stepTitle}>{t('stepAiProcesses')}</h3>
              <p className={styles.stepDesc}>{t('stepAiProcessesDesc')}</p>
            </li>
            <li className={styles.step}>
              <div className={styles.stepNum}>3</div>
              <h3 className={styles.stepTitle}>{t('stepViewInsights')}</h3>
              <p className={styles.stepDesc}>{t('stepViewInsightsDesc')}</p>
            </li>
          </ol>
        </div>
      </section>

      {/* Features */}
      <section id="features" className={styles.features}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>{t('insightsAndReports')}</h2>
          <div className={styles.featureGrid}>
            <article className={styles.featureCard}>
              <span className={styles.featureChip}>
                <Icon name="fa-receipt" size="md" />
              </span>
              <h3 className={styles.featureTitle}>{t('moduleReceiptsTitle')}</h3>
              <p className={styles.featureText}>{t('moduleReceiptsLong')}</p>
            </article>
            <article className={styles.featureCard}>
              <span className={`${styles.featureChip} ${styles.featureChipSuccess}`}>
                <Icon name="fa-credit-card" size="md" />
              </span>
              <h3 className={styles.featureTitle}>{t('moduleBudgetTitle')}</h3>
              <p className={styles.featureText}>{t('moduleBudgetLong')}</p>
            </article>
            <article className={styles.featureCard}>
              <span className={`${styles.featureChip} ${styles.featureChipWarning}`}>
                <Icon name="fa-chart-pie" size="md" />
              </span>
              <h3 className={styles.featureTitle}>{t('insightsAndReports')}</h3>
              <p className={styles.featureText}>{t('insightsAndReportsSubtitle')}</p>
            </article>
            <article className={styles.featureCard}>
              <span className={`${styles.featureChip} ${styles.featureChipDanger}`}>
                <Icon name="fa-key" size="md" />
              </span>
              <h3 className={styles.featureTitle}>{t('privacyFirst')}</h3>
              <p className={styles.featureText}>{t('privacyFirstSubtitle')}</p>
            </article>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className={styles.footerCta}>
        <div className={styles.container}>
          <h3 className={styles.footerCtaTitle}>{t('readyToStart')}</h3>
          <p className={styles.footerCtaText}>{t('readyToStartSubtitle')}</p>
          <Button variant="primary" size="lg" onClick={handleTryApp}>
            {t('getStartedFree')}
          </Button>
        </div>
      </section>

      <footer className={styles.footer}>
        <div className={styles.container}>
          <Link to="/privacy" className={styles.footerLink}>{t('privacy.link')}</Link>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
